// Dérivation des balises SEO — fonction pure (Phase 96, D-09 / §Q3).
//
// Toute la logique vit ici, et pas dans le composant : c'est ce qui permet de
// prouver la réciprocité des `hreflang` sur la table ENTIÈRE sans monter jsdom.
// `SeoHead.tsx` ne fait que rendre ce que cette fonction rend.
//
// `noindex`, `hreflang` et sitemap sortent tous des MÊMES deux drapeaux d'une
// MÊME ligne de `routes.ts` : ils sont structurellement incapables de diverger.
// Ne jamais réécrire l'un des trois à partir d'une autre source.
//
// ⚠️ L'ORIGINE EST EN DUR, ET CE N'EST PAS UN OUBLI. Lire l'origine du
// document au moment de l'exécution est la « simplification » évidente, et
// elle casserait l'indexation : `preprod.sweatstudio.fr` et
// `staging.sweatstudio.fr` servent tous deux `Allow: /`, donc ils sont
// crawlables. Avec l'origine du document, chaque page de miroir
// s'auto-canonicaliserait et deviendrait un doublon indexable du site. Avec
// l'origine de production figée, les miroirs pointent vers la prod — le
// comportement recommandé pour un miroir (T-96-12).
// Refs: phase 96.
import { findRoute, PUBLIC_ROUTES } from './routes'
import { withLang } from '../i18n/urlLang'
import type { PublicRoute } from './routes'
import type { Lang } from '../i18n/types'

/** Origine de PRODUCTION, jamais celle du document. Voir l'en-tête. */
export const SEO_ORIGIN = 'https://sweatstudio.fr'

export type SeoLinks = {
  /** URL absolue de la page dans la langue demandée. */
  canonical: string
  /** Vide, ou exactement 3 entrées — jamais un sous-ensemble. */
  alternates: { hreflang: string; href: string }[]
  noindex: boolean
}

/**
 * Balises SEO d'un chemin NU (déjà dépréfixé par le `basename` du routeur).
 *
 * Liste blanche par défaut (T-96-01) : un chemin absent de `PUBLIC_ROUTES`
 * est traité comme non indexable. Un chemin oublié tombe du côté sûr — jamais
 * l'inverse.
 *
 * D-09 : une jumelle `/en/` dont la page n'est pas traduite porte `noindex` et
 * AUCUN `hreflang`. Annoncer un `hreflang` vers une page désindexée envoie deux
 * signaux contradictoires, ce que Google ignore en silence.
 *
 * `routes` rend cette fonction RÉELLEMENT pure : elle dérive de la table qu'on
 * lui donne, au lieu de lire une constante de module. Le défaut reste
 * `PUBLIC_ROUTES`, et il est écrit dans la signature pour qu'aucun appelant ne
 * puisse hériter d'une table vide par accident (D-04, phase 99). Aucun appelant
 * de production ne passe l'argument.
 */
export function seoLinks(
  barePath: string,
  lang: Lang,
  routes: readonly PublicRoute[] = PUBLIC_ROUTES,
): SeoLinks {
  const canonical = SEO_ORIGIN + withLang(barePath, lang)
  const route = findRoute(barePath, routes)

  // Absente de la table, ou explicitement hors index : rien à annoncer.
  if (!route?.indexable) return { canonical, alternates: [], noindex: true }

  // Indexable mais pas encore traduite : la version française reste indexable,
  // sa jumelle anglaise existe et fonctionne, mais elle n'est pas annoncée.
  // La Phase 97 lève le drapeau page par page, et le contrôle 2 de
  // `routes.test.ts` fait échouer la suite si on l'oublie.
  if (!route.translated) return { canonical, alternates: [], noindex: lang === 'en' }

  // Le triplet est IDENTIQUE des deux côtés de la paire — c'est la définition
  // même de la réciprocité, et elle est ici structurelle : il ne dépend pas de
  // `lang`. `x-default` pointe le français, la racine étant l'entrée canonique
  // du site.
  const french = SEO_ORIGIN + withLang(barePath, 'fr')
  return {
    canonical,
    alternates: [
      { hreflang: 'fr', href: french },
      { hreflang: 'en', href: SEO_ORIGIN + withLang(barePath, 'en') },
      { hreflang: 'x-default', href: french },
    ],
    noindex: false,
  }
}
